import { DocumentEventsService } from 'src/app/shared/services/document-events/document-events.service';
import {
  AfterViewInit,
  Component,
  computed,
  ElementRef,
  EventEmitter,
  Input,
  input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { BaseFormControlComponent } from '../../../classes/base-form-control.component';
import { EditorModule } from '@progress/kendo-angular-editor';
import { IconsModule } from '@progress/kendo-angular-icons';
import { InputsModule, SignatureModule } from '@progress/kendo-angular-inputs';
import { FileSelectModule } from '@progress/kendo-angular-upload';
import { SignatureComponent } from '@progress/kendo-angular-inputs/signature/signature.component';
import { CollectionFormEditApiService } from '../../../../../../api/bizzmine/collection-form-edit/collection-form-edit-api.service';
import { asyncScheduler, debounceTime, lastValueFrom } from 'rxjs';

import { CanvasCustomToolbarModule } from './custom-canvas-toolbar-buttons/canvas-custom-toolbar.module';
import {
  CustomCanvasActions,
  CustomCanvasActionsComponent
} from './custom-canvas-toolbar-buttons/custom-canvas-actions/custom-canvas-actions.component';
import { CustomCanvasToolbarColorpickerComponent } from './custom-canvas-toolbar-buttons/custom-canvas-toolbar-colorpicker/custom-canvas-toolbar-colorpicker.component';
import { CustomCanvasToolbarDrawLineComponent } from './custom-canvas-toolbar-buttons/custom-canvas-toolbar-draw-line/custom-canvas-toolbar-draw-line.component';
import { CustomCanvasFileUploadComponent } from './custom-canvas-toolbar-buttons/custom-canvas-file-upload/custom-canvas-file-upload.component';
import SignaturePad from 'signature_pad';
import { CustomCanvasToolbarButtonComponent } from './custom-canvas-toolbar-buttons/custom-canvas-toolbar-button/custom-canvas-toolbar-button.component';
import { CollectionFormField } from '../../../../../../../models/ts/collection-form-field.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ContextMenuComponent, ContextMenuSelectEvent, MenuItemComponent } from '@progress/kendo-angular-menu';
import { TranslatePipe } from '../../../../../../shared/pipes/translate/translate.pipe';
import { FormControl } from '@angular/forms';

/**
 * Represents a control that allows the user to draw on a canvas.
 */
@Component({
  selector: 'bizz-canvas-control',
  standalone: true,
  imports: [CommonModule,
    EditorModule,
    InputsModule,
    FileSelectModule,
    IconsModule,
    SignatureModule,
    CanvasCustomToolbarModule,
    CustomCanvasToolbarDrawLineComponent,
    CustomCanvasToolbarColorpickerComponent,
    CustomCanvasActionsComponent,
    CustomCanvasFileUploadComponent, CustomCanvasToolbarButtonComponent, ContextMenuComponent, MenuItemComponent, TranslatePipe
  ],
  templateUrl: './canvas-control.component.html',
  styleUrls: ['./canvas-control.component.scss']
})
export class CanvasControlComponent extends BaseFormControlComponent implements OnInit, AfterViewInit, OnDestroy {
  public value: string | null = null;
  // Use theme colors
  public color = 'rgba(0, 0, 0, 1)';
  public strokeWidth = 1;
  public canvasHeight = 200;
  @ViewChild('signature') public signatureElement: SignatureComponent;
  @ViewChild('colorPickerToolbar') public colorPickerToolbar: CustomCanvasToolbarColorpickerComponent;
  @Input() public override formControl: FormControl;
  @Input() public defaultImage: string | undefined = undefined;
  @Input() public ngModel: string | undefined;
  private dirty = false;

  @Input() public signatureMode = false;
  @Output() public ngModelChange = new EventEmitter<string>();
  @ViewChild('kendoContextMenu') public kendoContextMenu: ContextMenuComponent;

  public fallBackImage: string | null = null;
  public signaturePad: SignaturePad;
  public imgWidth = 0;
  public hasImg = false;
  public hiddenProcessCanvasId = 'hiddenProcessCanvas';
  public canDraw = false;
  public canEditImage = false;
  public showToolbarButtons = false;
  public actions: Array<CustomCanvasActions> = [];
  public fileSelectionError: string | null = null;
  @ViewChild('SignaturePadElement') private signaturePadElement: ElementRef<HTMLCanvasElement>;
  @ViewChild('SignaturePadImageElement') private signaturePadImageElement: ElementRef<HTMLImageElement>;
  private currentSize = {
    width: 0,
    height: 0
  };
  public isMobileView: boolean = false;

  public setDirty(dirty: boolean): void {
    this.dirty = dirty;
    if(this.formControl) {
      if(dirty) {
        this.formControl.markAsDirty();
      } else {
        this.formControl.markAsPristine();
      }
    }
  }

  public constructor(
    private collectionApiService: CollectionFormEditApiService,
    private documentEventsService: DocumentEventsService) {
    super();
    this.documentEventsService.windowResizeEvent
      .pipe(takeUntilDestroyed(this.destroyRef),
        debounceTime(150))
      .subscribe(() => this.redraw());
    this.documentEventsService.isMobile.pipe(
      takeUntilDestroyed(this.destroyRef)).subscribe((isMobile: boolean) => {
        this.isMobileView = isMobile;
      }
    );
  }

  public override ngAfterViewInit(): void {
    super.ngAfterViewInit();

    this.initCanvas();
  }

  public ngOnDestroy(): void {
    if (this.signaturePad) {
      this.signaturePad.removeEventListener('beginStroke', null);
      this.signaturePad.removeEventListener('endStroke', null);
    }
  }

  public initCanvas(): void {
    if (this.signaturePadImageElement != undefined && this.signaturePadImageElement.nativeElement.width > 0) {
      if (this.signaturePad != undefined) {
        return;
      }
      this.signaturePad = new SignaturePad(this.signaturePadElement.nativeElement, {});
      this.signaturePad.addEventListener('beginStroke', () => {
        this.setDirty(true);
          if (this.colorPickerToolbar) {
            this.colorPickerToolbar.close();
          }
        },
        {
          passive: true
        }
      );
      this.signaturePad.penColor = this.color;
      this.onStrokeWidthChange(this.strokeWidth);

      this.signaturePad.addEventListener('endStroke', () => {
          this.mergeCanvasWithImage();
        },
        {
          passive: true
        });
      if (this.value != null && this.value.trim() != '') {
        this.setImageFromBase64(this.value, true);
      } else {
        this.setWithoutImage();
      }
    } else {
      asyncScheduler.schedule(() => {
        this.initCanvas();
      }, 10);
    }
  }

  public revertImage(): void {
    if (this.formFieldSignal()?.DefaultImage != null) {
      this.setDirty(true);
      this.setImageFromBase64(this.formFieldSignal()!.DefaultImage);
    } else if (this.defaultImage?.trim() != '') {
      this.setDirty(true);
      this.setImageFromBase64(this.defaultImage!);
    }
  }

  public removeStrokes(): void {
    this.setDirty(true);
    this.signaturePad.clear();
    this.mergeCanvasWithImage();
  }

  public removeImage(): void {
    this.setDirty(true);
    this.setWithoutImage();
  }

  public removeAll(): void {
    this.setDirty(false);

    if (this.formControl) {
      this.formControl.setValue(null);
    }
    this.signaturePad.clear();
    this.setWithoutImage();
  }

  public setNewColor(color: string): void {
    this.signaturePad.penColor = color;
    this.signaturePad.compositeOperation = 'source-over';

    if (!this.color) {
      this.signaturePad.compositeOperation = 'destination-out';
    }
  }

  public undo(): void {
    const data = this.signaturePad.toData();
    if (data) {
      data.pop(); // remove the last dot or line
      this.signaturePad.fromData(data);
    }
  }

  public override ngOnInit(): void {
    if (this.formFieldSignal()) {
      super.ngOnInit();
    }

    this.strokeWidth = this.configOverrides()?.DefaultStrokeWidth ?? 1;

    const cloned = structuredClone(this.formFieldSignal());

    if (this.isInGrid && cloned)
      cloned.CanvasHeight = 100;

    if (cloned) {
      this.value = cloned?.Value;
      this.hiddenProcessCanvasId = this.hiddenProcessCanvasId + cloned.Id;
      this.fallBackImage = this.value;


      if (cloned?.CanvasHeight > 0) {
        this.canvasHeight = cloned.CanvasHeight;
      }
      if (this.value == null || this.value.trim() == '') {
        this.value = cloned.DefaultImage;
      }
    } else {
      this.value = this.ngModel ?? this.formControl?.value;
      if (this.value == undefined && this.defaultImage?.trim() != '') {
        this.value = this.defaultImage ?? '';
      }
    }
  }


  public redrewAfterToolBarChanged = false;
  public configOverrides = input<{
    CanUseToolbar?: boolean,
    CanDraw: boolean,
    CanEditImage: boolean,
    IsReadOnly: boolean,
    CanvasHeight: number,
    FullWidth?: boolean,
    DefaultStrokeWidth?: number,
  }>();

  public canUseToolbar = computed(() => {
    const formField = this.formFieldSignal() ?? this.configOverrides();
    this.canEditImage = false;
    this.canDraw = false;
    this.actions = [];
    if (formField == null) {
      return false;
    }

    this.showToolbarButtons = formField.CanUseToolbar ?? false;

    if ((formField.CanUseToolbar || formField.CanDraw || formField.CanEditImage) && formField?.IsReadOnly != true && !this.readOnlyOverride) {
      if (formField.CanDraw) {
        this.canDraw = true;
        this.actions.push(CustomCanvasActions.RemoveImage);
        this.actions.push(CustomCanvasActions.RemoveStrokes);

        if (formField.CanEditImage) {
          this.actions.push(CustomCanvasActions.RemoveAll);
        }
      }

      if (formField.CanEditImage) {
        this.canEditImage = true;
        this.actions.push(CustomCanvasActions.RevertImage);
      }
    }

    if (!this.redrewAfterToolBarChanged) {
      this.redrewAfterToolBarChanged = true;
      asyncScheduler.schedule(() => {
        this.redraw();
      });
    }
    return this.canEditImage || this.canDraw;
  });

  protected override valueSetter(field: CollectionFormField): void {
    const diffentValue = this.value != field.Value;
    if (!diffentValue) return;
    this.value = field.Value;
    if (this.value != null && this.value.trim() != '') {
      this.setImageFromBase64(this.value);
    } else {
      this.setWithoutImage();
    }
  }

  public onStrokeWidthChange($event: number): void {
    this.signaturePad.dotSize = $event * 0.75;
    this.signaturePad.minWidth = this.signaturePad.dotSize * 0.5;
    this.signaturePad.maxWidth = this.signaturePad.dotSize;
  }

  public onImageUploadError(error: string): void {
    this.fileSelectionError = error;

    //display error for 15 seconds - then clear
    setTimeout(() => {
      this.fileSelectionError = null;
    }, 15000);
  }

  public onImageUpload(file: File): void {
    this.setDirty(true);
    this.readFile(file);
  }

  public readFile(file: File): void {
    const reader = new FileReader();

    const onLoad = (e: any) => {
      const arrayBuffer = e.target.result as ArrayBuffer; // Get array buffer
      const blob = new Blob([arrayBuffer], { type: 'image/jpeg' }); // Create JPEG blob
      const jpegUrl = URL.createObjectURL(blob); // Create temporary URL

      // some complex logic abusing canvas to get the JPEG data (convert)
      const img = new Image();
      img.onload = () => {
        const canvas = document.getElementById(this.hiddenProcessCanvasId) as HTMLCanvasElement;
        if (!canvas) return;
        canvas.style.visibility = 'hidden';
        canvas.width = img.width;
        canvas.height = img.height;

        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0);
        }
        const jpegData = canvas.toDataURL('image/jpeg', 0.8); // Get base64 JPEG data

        this.readImage(jpegData).then().finally(() => canvas.style.visibility = 'visible'); // Pass base64 data to your function

        URL.revokeObjectURL(jpegUrl); // Clean up temporary URL
        img.src = ''; // Clear image source to release memory
        img.removeEventListener('load', onLoad); // Remove event listener
        reader.removeEventListener('load', onLoad); // Remove event listener
      };
      img.src = jpegUrl;
    };

    reader.addEventListener('load', onLoad,
      {
        passive: true
      });
    reader.readAsArrayBuffer(file); // Read as array buffer for conversion
  }

  //function to download canvas
  public canvasCustomContextMenu(event: ContextMenuSelectEvent): void {
    if (event.item.data == 'Download') {
      const link = document.createElement('a');
      link.download = this.formFieldSignal()?.Caption + '.jpeg';
      link.href = this.formControl.value;
      link.click();
    }
  }

  public readImage(imageBase64: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const image = new Image();

      const onLoad = () => {
        image.removeEventListener('load', onLoad);
        lastValueFrom(this.collectionApiService.resizeImage(image.src))
          .then((res) => {
            this.value = res;
            if (res != null) {
              this.setImageFromBase64(res);
            }
            this.fallBackImage = res;
            resolve();
          })

          .catch(reject); // Handle errors from resizeImage
      };

      image.addEventListener('load', onLoad,
        {
          passive: true
        });
      image.src = imageBase64;

      image.onerror = (error) => {
        reject(error); // Reject the Promise on image load error
      };
    });
  }

  private redraw(): void {
    const image = this.signaturePadImageElement.nativeElement;
    const canvas = this.signaturePadElement.nativeElement;
    const found = this.signaturePad.toData();
    const ratioX = image.width / canvas.width;
    const ratioY = image.height / canvas.height;
    for (let i = 0; i < found.length; i++) {
      for (let j = 0; j < found[i].points.length; j++) {
        found[i].points[j].x = found[i].points[j].x * ratioX;
        found[i].points[j].y = found[i].points[j].y * ratioY;
      }
    }
    canvas.style.width = image.width + 'px';
    canvas.style.height = image.height + 'px';
    canvas.width = image.width;
    canvas.height = image.height;
    this.signaturePad.fromData(found);
  }

  private setWithoutImage(): void {
    const h = this.formFieldSignal()?.CanvasHeight ?? this.configOverrides()?.CanvasHeight;
    if (h) {
      this.signaturePadImageElement.nativeElement.src = '';
      this.hasImg = false;
      const w = this.signaturePadImageElement.nativeElement.width;
      const image = this.signaturePadImageElement.nativeElement;
      const canvas = this.signaturePadElement.nativeElement;
      if (image && canvas) {
        image.height = h;
        image.style.height = h + 'px';
        image.width = w;
        image.style.width = w + 'px';

        canvas.height = h;
        canvas.width = w;
      }
      setTimeout(() => {
        // handle last on stack -> making sure html is rendered completely
        this.mergeCanvasWithImage();
      }, 0);
    }
  }

  private setImageFromBase64(base64: string, preventChangeEvent = false): void {
    const img = new Image();
    img.onload = () => {
      this.imgWidth = img.width;
      this.signaturePadImageElement.nativeElement.style.height = 'auto';
      this.signaturePadImageElement.nativeElement.src = base64;
      this.hasImg = true;
      this.signaturePadImageElement.nativeElement.onload = () => {
        const w = this.signaturePadImageElement.nativeElement.width;
        const h = this.signaturePadImageElement.nativeElement.height;
        const c = this.signaturePadElement.nativeElement;
        if (c) {
          c.height = h;
          c.width = w;
        }
      };
      setTimeout(() => {
        // handle last on stack -> making sure html is rendered completely
        this.mergeCanvasWithImage(preventChangeEvent);
      }, 0);

    };
    img.src = base64;
  }

  private mergeCanvasWithImage(preventChangeEvent = false): void {
    const canvasRef: HTMLCanvasElement = document.getElementById(this.hiddenProcessCanvasId) as HTMLCanvasElement;
    const context = canvasRef.getContext('2d');
    if (canvasRef && context) {
      if (this.value == null || !this.hasImg) {
        this.setBlankCanvasImage();
      } else {
        const backgroundImage = new Image();
        const drawings = new Image();

        drawings.onload = (): void => {
          context.drawImage(backgroundImage, 0, 0);
          context.drawImage(drawings, 0, 0);

          //if the image is a png, we need to convert it to png otherwise use jpg
          //This is needed to prevent black images because of transparent pngs to jpeg conversion
          // See: https://be.bizzmine.cloud/collection/28/instance/2164/version/2164/readonly?checksum=ef0c9cfc-6b8b-427b-b5ce-977d7055ce36
          const imageType =
            this.signaturePadImageElement.nativeElement.src.indexOf('image/png') > -1 ? 'image/png' : 'image/jpeg';

          const hasChanges = !preventChangeEvent && this.value != context.canvas.toDataURL(imageType);
          this.value = context.canvas.toDataURL(imageType);
          if (this.formControl && this.dirty) {
            this.formControl.setValue(this.value);
          }
          if (hasChanges && this.dirty) {
            this.ngModelChange.emit(this.value);
          }
          drawings.removeEventListener('load', () => {
          });
        };

        backgroundImage.onload = (): void => {
          canvasRef.width = backgroundImage.width;
          canvasRef.height = backgroundImage.height;
          canvasRef.style.height = backgroundImage.height + 'px';
          canvasRef.style.width = backgroundImage.width + 'px';
          context.drawImage(this.signaturePadElement.nativeElement, 0, 0, backgroundImage.width, backgroundImage.height);
          drawings.src = context.canvas.toDataURL('image/png');
          backgroundImage.removeEventListener('load', () => {
          });
        };

        backgroundImage.src = this.signaturePadImageElement.nativeElement.src;
        this.hasImg = true;
        asyncScheduler.schedule(() => {
          this.redraw();
        });
      }
    }
  }

  public setBlankCanvasImage(): void {
    const canvasRef: HTMLCanvasElement = document.getElementById(this.hiddenProcessCanvasId) as HTMLCanvasElement;
    const context = canvasRef.getContext('2d');
    const img = new Image();
    if (canvasRef && context) {
      img.onload = (): void => {
        canvasRef.width = img.width;
        canvasRef.height = img.height;
        canvasRef.style.height = img.height + 'px';
        canvasRef.style.width = img.width + 'px';
        context.fillStyle = '#FFFFFF';
        context.fillRect(0, 0, img.width, img.height);
        context.drawImage(img, 0, 0, img.width, img.height);
        this.value = context.canvas.toDataURL('image/jpeg');
        if (this.formControl && this.dirty) {
          this.formControl.setValue(this.value);
        }
        // sets the background as image -> making for a good resize experience
        this.setImageFromBase64(this.value);
        asyncScheduler.schedule(() => {
          this.redraw();
        });
      };
      img.src = this.signaturePad.toDataURL();
    }
  }

  protected override focus(): void {
    // Add your implementation here
  }
}

